import API from "../constants/api.constant";
import axiosInstance from "./axiosInstance";
import { UploadLogoData, UploadNcnndaAgreementData, UploadSignatureData } from "../validation/business.validation";
import {responseErrorHandling} from "../helper/common.helper";

export interface DashboardData {
  totalLeads: number;
  totalProspects: number;
  totalCustomers: number;
  totalDeals: number;
}

export const businessUploadSignature = async (data: UploadSignatureData) => {
  try {
    const response = await axiosInstance.put(
      API.UPLOAD_SIGNATURE_ENDPOINT + `/${data.id}`,
      { signature: data.signature }
    );
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
};

export const businessUploadNcnndaAgreement = async (data: UploadNcnndaAgreementData) => {
  try {
    const response = await axiosInstance.put(
      API.UPLOAD_NCNDA_AGREEMENT_ENDPOINT + `/${data.id}`,
      {
        signature: data.signature,
        agreementPdfPath: data.agreementPdfPath,
      }
    );
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
};

// upload business logo
export const businessUploadLogo = async (data: UploadLogoData) => {
  try {
    const formData = new FormData();
    formData.append("file", data.logo);

    const response = await axiosInstance.put(
      API.UPLOAD_LOGO_ENDPOINT + `/${data.id}`,
      formData
    );
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
};

// get business profile
export const getBusinessProfile = async (id: String) => {
    try {
    const response = await axiosInstance.get(
      API.GET_BUSINESS_PROFILE + id,
    );
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

// update business profile
export const updateBusinessProfile = async (id: String, data:Object) => {
  try {
    const response = await axiosInstance.patch(API.UPDATE_BUSINESS_PROFILE + id, data);
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error)
  }
}

// change business password
export const changeBusinessPassword = async ( id: string, data: object) => {
 try {
  const response = await axiosInstance.put(API.BUSINESS_PASSWORD_CHANGE + id, data);
  return response.data;
 } catch (error:any) {
  responseErrorHandling(error)
 }
}

// coOwner invitations link
export const coOwnerInvitationLink = async (businessId: string) => {
 try {
   const response = await axiosInstance.get(API.CO_OWNER_INVITATION_LINK + businessId);
   return response.data;
 } catch (error) {
    responseErrorHandling(error)
 }
}

// send CoOwner registration invite
export const sendRegisterInvitationCoOwner = async (data: object) => {
  try {
   const response = await axiosInstance.post(API.SEND_REGISTER_INVITE_CO_OWNER, data);
   return response.data;
  } catch (error) {
    responseErrorHandling(error)
  }
}

// Get dashboard data for business
export const getDashboardData = async (months: number = 3): Promise<DashboardData> => {
  try {
    const response = await axiosInstance.get<DashboardData>(API.BUSINESS_DASHBOARD_DATA, {
      params: { months }
    });
    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
    return { totalLeads: 0, totalProspects: 0, totalCustomers: 0, totalDeals: 0 };
  }
};

export interface LinkedInPostResponse {
  message: string;
  linkedInPostId: string;
  linkedInAsset: string;
  imageUrl: string;
  publicId: string;
}

export const publishLinkedInImagePost = async (
  image: File,
  caption: string,
): Promise<LinkedInPostResponse | undefined> => {
  try {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("caption", caption);

    const response = await axiosInstance.post<LinkedInPostResponse>(
      API.BUSINESS_LINKEDIN_POST,
      formData,
    );

    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
  }
};

export interface LinkedInAuthUrlResponse {
  url: string;
}

export interface LinkedInStatusResponse {
  connected: boolean;
  displayName?: string;
  expiresAt?: string | null;
}

export const getLinkedInAuthUrl = async (): Promise<LinkedInAuthUrlResponse | undefined> => {
  try {
    const response = await axiosInstance.get<LinkedInAuthUrlResponse>(
      API.BUSINESS_LINKEDIN_AUTH_URL,
    );

    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
  }
};

export const getLinkedInConnectionStatus = async (): Promise<LinkedInStatusResponse> => {
  try {
    const response = await axiosInstance.get<LinkedInStatusResponse>(
      API.BUSINESS_LINKEDIN_STATUS,
    );

    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
    return { connected: false };
  }
};

export const disconnectLinkedInAccount = async (): Promise<LinkedInStatusResponse> => {
  try {
    const response = await axiosInstance.delete<LinkedInStatusResponse>(
      API.BUSINESS_LINKEDIN_DISCONNECT,
    );

    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
    return { connected: true };
  }
};

export interface InstagramPostResponse {
  message: string;
  instagramPostId: string;
  instagramCreationId: string;
  imageUrl: string;
  publicId: string;
}

export const publishInstagramImagePost = async (
  image: File,
  caption: string,
): Promise<InstagramPostResponse | undefined> => {
  try {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("caption", caption);

    const response = await axiosInstance.post<InstagramPostResponse>(
      API.BUSINESS_INSTAGRAM_POST,
      formData,
    );

    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
  }
};

export const getInstagramAuthUrl = async (): Promise<LinkedInAuthUrlResponse | undefined> => {
  try {
    const response = await axiosInstance.get<LinkedInAuthUrlResponse>(
      API.BUSINESS_INSTAGRAM_AUTH_URL,
    );

    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
  }
};

export const getInstagramConnectionStatus = async (): Promise<LinkedInStatusResponse> => {
  try {
    const response = await axiosInstance.get<LinkedInStatusResponse>(
      API.BUSINESS_INSTAGRAM_STATUS,
    );

    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
    return { connected: false };
  }
};

export const disconnectInstagramAccount = async (): Promise<LinkedInStatusResponse> => {
  try {
    const response = await axiosInstance.delete<LinkedInStatusResponse>(
      API.BUSINESS_INSTAGRAM_DISCONNECT,
    );

    return response.data;
  } catch (error: any) {
    responseErrorHandling(error);
    return { connected: true };
  }
};
